import { useContext, useEffect } from "react";
import { OrderIdContext, StageContext } from "../contexes/OrderIdContext";
import { AccountingEventsGroup } from "../types/events";
import {
  DigitalBusinessDocument,
  DigitalCostDocument,
} from "../types/documents";
import { useMarketplaceId } from "../contexes/MarketplaceIdContext";
import { getCountryCodeFromMarketplace } from "../helpers/marketplaceUtils";
import { useHealthStatus } from "./useHealthStatus";
import {
  isAccountingEventsGroup,
  isDigitalBusinessDocument,
  isDigitalCostDocument,
} from "../helpers/typeChecks";
import { DlqInfo } from "./useFetchDlqInfo";
import { useGlProduct } from "../contexes/GlProductContext";
import { getPaymentMethod } from "../helpers/AccountingEventsUtils";
import { getDbdPaymentMethodName } from "../helpers/PaymentUtils";
import {
  useHealthState,
  useHealthStateUpdate,
} from "../contexes/HealthStateContext";
import { isFreeTrial } from "../helpers/freeTrialUtils";
import { shouldHideAccountingFlow } from "../helpers/flowTypeUtils";

export const GL_MAPPING: Record<string, string> = {
  "318": "TVOD",
  "613": "Channels",
  "628": "SVOD",
  "796": "SWM",
};

export const getGlMapping = (gl: number | string): string => {
  if (typeof gl === "string" && gl.includes("(")) {
    return gl;
  }
  const glString = gl.toString();
  const glName = GL_MAPPING[glString] || glString;
  return `${glName} (${glString})`;
};
export function useSummaryData(
  eventData: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
  )[],
  dlqInfo: DlqInfo
) {
  const stage = useContext(StageContext);
  const orderId = useContext(OrderIdContext);
  const { marketplaceId } = useMarketplaceId();
  const { setGlProduct } = useGlProduct();
  const countryCode = getCountryCodeFromMarketplace(marketplaceId);

  const { healthSteps, flowSteps } = useHealthStatus(dlqInfo.status);

  const healthStateContext = useHealthState();
  const { setState: setHealthState } = useHealthStateUpdate();
  const healthStatus = healthStateContext.overallHealth;

  // Show loading spinner when health status is unknown (initial state) or DLQ info is still loading
  const isHealthLoading = healthStatus === "unknown";

  const freeTrialStatus = isFreeTrial(eventData);

  const orderDetails = (() => {
    if (!eventData || eventData.length === 0) {
      return {
        orderDate: undefined,
        glProductGroup: undefined,
        consumptionType: undefined,
        fulfillmentType: undefined,
        lastItem: undefined,
        paymentMethod: undefined,
      };
    }

    // Iterate in reverse to use latest versions first (as have the most data)
    for (let i = eventData.length - 1; i >= 0; i--) {
      const event = eventData[i];

      // Handle DBD and DCDs first
      if (isDigitalBusinessDocument(event)) {
        const firstProduct = Object.values(event.body.body.products)[0];
        const paymentId = Object.keys(event.body.body.payments || {})[0];
        const payment = event.body.body.payments?.[paymentId];
        const method = payment?.paymentMethods?.[paymentId];

        return {
          orderDate: event.body.header.transactionDate,
          glProductGroup: firstProduct?.details.glProductLineId,
          consumptionType: firstProduct?.details.activity,
          fulfillmentType:
            event.body.metadata.internalAttributes?.FULFILLMENT_TYPE,
          lastItem: { digitalBusinessDocument: event },
          paymentMethod: method ? getDbdPaymentMethodName(method) : undefined,
        };
      }

      if (isDigitalCostDocument(event)) {
        return {
          orderDate: event.body.header.transactionDate,
          glProductGroup: event.body.body.glProductLineId,
          consumptionType: event.body.body.cost.transactionType,
          lastItem: { digitalCostDocument: event },
        };
      }

      if (isAccountingEventsGroup(event)) {
        const revenueEvent = Object.values(event.accountingEvents).find(
          (event) => event.type === "REVENUE"
        );
        const fallbackEvent = Object.values(event.accountingEvents).find(
          (event) => event.type === "ROYALTY"
        );
        const selectedEvent = revenueEvent || fallbackEvent;

        // Only return accounting events if they shouldn't be hidden
        if (
          !shouldHideAccountingFlow(
            eventData,
            selectedEvent?.glProductGroup?.toString()
          )
        ) {
          return {
            orderDate: selectedEvent?.orderDate,
            glProductGroup: selectedEvent?.glProductGroup,
            consumptionType: revenueEvent?.consumptionType,
            lastItem: { accountingEventsGroup: event },
            paymentMethod: revenueEvent
              ? getPaymentMethod(revenueEvent)
              : undefined,
          };
        }
      }
    }

    return {
      orderDate: undefined,
      glProductGroup: undefined,
      consumptionType: undefined,
      paymentMethod: undefined,
      lastItem: undefined,
    };
  })();

  setGlProduct(
    orderDetails.glProductGroup
      ? getGlMapping(orderDetails.glProductGroup.toString())
      : "unknown"
  );

  useEffect(() => {
    setHealthState((prevState) => ({
      ...prevState,
      isFreeTrial: freeTrialStatus,
    }));
  }, [freeTrialStatus, setHealthState]);

  return {
    stage,
    orderId,
    countryCode,
    healthStatus,
    healthSteps,
    orderDate: orderDetails.orderDate,
    glProductGroup: orderDetails.glProductGroup,
    consumptionType: orderDetails.consumptionType,
    fulfillmentType: orderDetails.fulfillmentType,
    paymentMethod: orderDetails.paymentMethod,
    isFreeTrial: freeTrialStatus,
    getGlMapping,
    lastItem: orderDetails.lastItem,
    isHealthLoading,
    flowSteps,
  };
}
